import { periodState, toDay } from './dates';
import { sprintShort } from './colors';

// Ce que la roadmap dit d'elle-même quand rien n'est ouvert : le sprint à regarder, le rythme de l'itération en
// cours, les prochains jalons et ce qui reste à préparer. Calculs seuls (testés), sans affichage.

const byStart = (a, b) => String(a.start_date || '').localeCompare(String(b.start_date || ''));
const stateOf = (item, today) => periodState(toDay(item.start_date), toDay(item.end_date), today);

/** Tous les sprints du projet, dans l'ordre, avec leur itération et leur libellé court (« 2.3 »). */
export const allSprints = (iterations = []) => iterations.slice().sort(byStart).flatMap((iteration) => (iteration.sprints || [])
  .map((sprint, index) => ({ ...sprint, iteration, short: sprintShort(sprint, index) })));

/** Le sprint à montrer : celui de la sélection, sinon celui en cours, sinon le prochain, sinon le dernier. */
export const focusSprint = (iterations = [], today, selection = null) => {
  const sprints = allSprints(iterations);
  if (!sprints.length) return null;
  if (selection?.sprintId) {
    const chosen = sprints.find((s) => s.id === selection.sprintId);
    if (chosen) return chosen;
  }
  const pool = selection?.iterationId ? sprints.filter((s) => s.iteration.id === selection.iterationId) : sprints;
  if (!pool.length) return null;
  return pool.find((s) => stateOf(s, today) === 'current')
    || pool.find((s) => stateOf(s, today) === 'upcoming')
    || pool[pool.length - 1];
};

/** Rythme d'une itération : part du temps écoulé contre part des points réalisés. */
export const pace = (iteration, today) => {
  const start = toDay(iteration.start_date);
  const end = toDay(iteration.end_date);
  const time = start != null && end != null && end > start ? Math.min(1, Math.max(0, (today - start) / (end - start))) : 0;
  const total = Number(iteration.total_tokens) || 0;
  const done = total > 0 ? Math.min(1, (Number(iteration.completed_tokens) || 0) / total) : null;
  let verdict = 'unknown';
  if (done != null) {
    const gap = done - time;
    verdict = gap < -0.2 ? 'late' : gap < -0.08 ? 'watch' : 'on_track';
  }
  return { time, done, total, verdict, daysLeft: end != null ? end - today : null };
};

/** Jalons non atteints, du plus pressé au plus lointain (les retards d'abord). */
export const nextMilestones = (objectives = [], today, limit = 4) => objectives
  .filter((o) => o.is_milestone && o.milestone_date && o.status !== 'done')
  .map((o) => ({ ...o, inDays: toDay(o.milestone_date) - today }))
  .sort((a, b) => a.inDays - b.inDays)
  .slice(0, limit);

/**
 * Ce qui reste à préparer. Chaque élément : { key, kind, text, target } où target dit quoi ouvrir
 * ({ iterationId, sprintId? } | 'backlog' | 'new-iteration').
 */
export const roadmapGaps = ({ iterations = [], objectives = [], billability = {}, canDecideBilling = false, today }) => {
  const gaps = [];
  const sorted = iterations.slice().sort(byStart);
  // Les itérations en cours (parfois plusieurs, une par axe) et la prochaine.
  const nearby = [
    ...sorted.filter((it) => stateOf(it, today) === 'current'),
    ...sorted.filter((it) => stateOf(it, today) === 'upcoming').slice(0, 1),
  ];

  nearby.forEach((iteration) => {
    const own = iteration.sprints || [];
    const current = stateOf(iteration, today) === 'current';
    if (current && !own.some((s) => ['current', 'upcoming'].includes(stateOf(s, today)))) {
      gaps.push({
        key: `no-sprint-${iteration.id}`,
        kind: 'sprints',
        text: own.length ? `${iteration.name} : aucun sprint en cours ni prévu` : `${iteration.name} : en cours sans sprint`,
        target: { iterationId: iteration.id },
      });
    }
    if (!String(iteration.description || '').trim()) {
      gaps.push({ key: `vision-${iteration.id}`, kind: 'vision', text: `${iteration.name} : vision à définir`, target: { iterationId: iteration.id } });
    }
    if (!current && !own.length) {
      gaps.push({ key: `sprints-${iteration.id}`, kind: 'sprints', text: `${iteration.name} : pas encore de sprint`, target: { iterationId: iteration.id } });
    }
  });

  const plannedSprints = allSprints(nearby).filter((s) => ['current', 'upcoming'].includes(stateOf(s, today)));
  const visionless = plannedSprints.filter((s) => !String(s.vision || '').trim());
  if (visionless.length) {
    const first = visionless[0];
    gaps.push({
      key: 'sprint-vision',
      kind: 'vision',
      text: visionless.length === 1
        ? `Sprint ${first.short} : vision à définir`
        : `${visionless.length} sprints sans vision (${visionless.slice(0, 3).map((s) => s.short).join(', ')}${visionless.length > 3 ? '…' : ''})`,
      target: { iterationId: first.iteration.id, sprintId: first.id },
    });
  }

  const current = allSprints(sorted).find((s) => stateOf(s, today) === 'current');
  if (current && !objectives.some((o) => o.sprint_id === current.id)) {
    gaps.push({ key: 'sprint-empty', kind: 'objectives', text: `Sprint ${current.short} : aucun objectif planifié`, target: 'backlog' });
  }

  const unplanned = objectives.filter((o) => !o.sprint_id && !o.is_milestone && o.status !== 'done').length;
  if (unplanned) {
    gaps.push({ key: 'backlog', kind: 'backlog', text: `${unplanned} objectif${unplanned > 1 ? 's' : ''} du backlog sans sprint`, target: 'backlog' });
  }

  const lastEnd = sorted.reduce((max, it) => Math.max(max, toDay(it.end_date) ?? -Infinity), -Infinity);
  if (Number.isFinite(lastEnd) && lastEnd >= today && lastEnd - today <= 30) {
    gaps.push({ key: 'horizon', kind: 'horizon', text: `La roadmap s'arrête dans ${lastEnd - today} j : prévois la suite`, target: 'new-iteration' });
  }

  if (canDecideBilling) {
    sorted.filter((it) => stateOf(it, today) === 'done').forEach((iteration) => {
      const decision = billability?.[iteration.id] ?? billability?.[String(iteration.id)];
      if (decision?.billable === true || decision?.billable === false) return;
      if (toDay(iteration.end_date) < today - 120) return; // les vieilles itérations ne comptent plus
      gaps.push({ key: `billing-${iteration.id}`, kind: 'billing', text: `${iteration.name} terminée : facturable ?`, target: { iterationId: iteration.id } });
    });
  }
  return gaps;
};
